import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import userBootstrapService from '../services/userBootstrapService';
import { getPhotoReviewStatus } from '../services/photoService';
import { useAuth } from './AuthContext';

const UnifiedContext = createContext(null);
const UNIFIED_CACHE_PREFIX = 'fs_unified_cache_v1';

const buildCacheKey = (userId) => {
  if (!userId) return null;
  return `${UNIFIED_CACHE_PREFIX}:${userId}`;
};

const readCachedUnified = (userId, ttlMs, { ignoreTtl = false } = {}) => {
  if (typeof window === 'undefined') return null;
  const key = buildCacheKey(userId);
  if (!key) return null;
  try {
    const raw = window.sessionStorage.getItem(key) || window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const { ts, data } = parsed;
    if (!ts || !data) return null;
    if (!ignoreTtl && Date.now() - ts > ttlMs) return null;
    return parsed;
  } catch (_) {
    return null;
  }
};

const writeCachedUnified = (userId, data) => {
  if (typeof window === 'undefined') return;
  const key = buildCacheKey(userId);
  if (!key) return;
  try {
    const payload = JSON.stringify({ ts: Date.now(), data });
    window.sessionStorage.setItem(key, payload);
    try {
      window.localStorage.setItem(key, payload);
    } catch (_) {
      // ignore quota / private mode
    }
  } catch (_) {
    try {
      window.localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
    } catch (_) {
      // no-op
    }
  }
};

const clearCachedUnified = (userId) => {
  if (typeof window === 'undefined') return;
  const key = buildCacheKey(userId);
  if (!key) return;
  try {
    window.sessionStorage.removeItem(key);
    window.localStorage.removeItem(key);
  } catch (_) {
    // no-op
  }
};

export const UnifiedProvider = ({ children, ttlMs = 5 * 60 * 1000 }) => {
  const [unified, setUnified] = useState(null);
  const [photoReviews, setPhotoReviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hydratedUserId, setHydratedUserId] = useState(null);
  const lastFetchedRef = useRef(0);
  const unifiedRef = useRef(null);
  const pendingRef = useRef(null);
  const { user, isAuthenticated } = useAuth();

  const refreshPhotoReviews = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      setPhotoReviews([]);
      return;
    }
    try {
      const { data } = await getPhotoReviewStatus(user.id);
      setPhotoReviews(Array.isArray(data) ? data : []);
    } catch {
      setPhotoReviews([]);
    }
  }, [isAuthenticated, user?.id]);

  const refreshUnified = useCallback(async (force = false) => {
    try {
      if (!isAuthenticated || !user?.id) {
        setUnified(null);
        setPhotoReviews([]);
        return null;
      }

      const now = Date.now();
      const cached = unifiedRef.current;
      const cacheValid = !force && cached && (now - lastFetchedRef.current) < ttlMs;
      if (cacheValid) {
        void refreshPhotoReviews();
        return cached;
      }

      if (pendingRef.current) {
        return pendingRef.current;
      }

      setLoading(true);
      setError(null);
      const pendingPromise = userBootstrapService.getUnified(force)
        .then((data) => {
          setUnified(data);
          unifiedRef.current = data;
          lastFetchedRef.current = Date.now();
          if (user?.id) {
            writeCachedUnified(user.id, data);
          }
          void refreshPhotoReviews();
          return data;
        })
        .catch((err) => {
          setError(err);
          // Keep last known snapshot if the network/API blips (saved venues, avatar, etc.).
          if (user?.id) {
            const stale = readCachedUnified(user.id, ttlMs, { ignoreTtl: true });
            if (stale?.data) {
              setUnified(stale.data);
              unifiedRef.current = stale.data;
              lastFetchedRef.current = stale.ts || 0;
            }
          }
          throw err;
        })
        .finally(() => {
          pendingRef.current = null;
          setLoading(false);
        });

      pendingRef.current = pendingPromise;
      return pendingPromise;
    } catch (e) {
      setError(e);
      setLoading(false);
      return null;
    }
  }, [isAuthenticated, ttlMs, user?.id, refreshPhotoReviews]);

  useEffect(() => {
    unifiedRef.current = unified;
  }, [unified]);

  // Hydrate from cache whenever the authenticated user changes
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setUnified(null);
      unifiedRef.current = null;
      lastFetchedRef.current = 0;
      setPhotoReviews([]);
      if (hydratedUserId) {
        clearCachedUnified(hydratedUserId);
      }
      setHydratedUserId(null);
      userBootstrapService.clear();
      return;
    }

    if (hydratedUserId === user.id) {
      return;
    }

    let cached = readCachedUnified(user.id, ttlMs);
    if (!cached?.data) {
      cached = readCachedUnified(user.id, ttlMs, { ignoreTtl: true });
    }
    if (cached?.data) {
      setUnified(cached.data);
      unifiedRef.current = cached.data;
      lastFetchedRef.current = cached.ts || 0;
    }
    setHydratedUserId(user.id);
  }, [hydratedUserId, isAuthenticated, ttlMs, user?.id]);

  // Refresh once hydration is complete so we get a fresh snapshot in the background
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      return;
    }
    if (hydratedUserId !== user.id) {
      return;
    }

    let backgroundId;
    if (!unifiedRef.current) {
      refreshUnified(false).catch(() => {});
    } else {
      backgroundId = window.setTimeout(() => {
        refreshUnified(true).catch(() => {});
      }, 1000);
    }

    return () => {
      if (backgroundId) {
        window.clearTimeout(backgroundId);
      }
    };
  }, [hydratedUserId, isAuthenticated, refreshUnified, user?.id]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id || hydratedUserId !== user.id) {
      return;
    }
    void refreshPhotoReviews();
  }, [hydratedUserId, isAuthenticated, refreshPhotoReviews, user?.id]);

  return (
    <UnifiedContext.Provider
      value={{
        unified,
        photoReviews,
        loading,
        error,
        lastFetched: lastFetchedRef.current,
        refreshUnified,
        refreshPhotoReviews,
      }}
    >
      {children}
    </UnifiedContext.Provider>
  );
};

export const useUnified = () => {
  const ctx = useContext(UnifiedContext);
  if (!ctx) throw new Error('useUnified must be used within UnifiedProvider');
  return ctx;
};
